import { useState } from 'react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import { Button, Container, Link, Multimedia, SectionText } from '@/atoms';

import type { FunctionComponent } from 'react';
import type { TypeGalleryTemplateProps } from './types';

const PopUpIframe = dynamic(
  () => import('@/atoms/multimedia/Iframe/PopUpIframe'),
  {
    ssr: false,
  }
);
/**
 * GalleryTemplate
 */
export const GalleryTemplate: FunctionComponent<TypeGalleryTemplateProps> = ({
  className = '',
  backgroundColorBottom,
  backgroundColorBottomMobile,
  backgroundColorTop,
  backgroundColorTopMobile,
  button,
  caption,
  cutLevel = 50,
  cutLevelMobile = 50,
  desc,
  multimediaProps,
  link,
  subTitle,
  textColor,
  title,
  index,
  id,
}: TypeGalleryTemplateProps) => {
  const [popUpState, setPopUpState] = useState(false);

  const popUpToggle = () => {
    setPopUpState(!popUpState);
  };

  return (
    <Container
      index={index}
      backgroundColorBottom={backgroundColorBottom}
      backgroundColorTopMobile={backgroundColorTopMobile}
      backgroundColorTop={backgroundColorTop}
      backgroundColorBottomMobile={backgroundColorBottomMobile}
      cutLevel={cutLevel === null ? 50 : cutLevel}
      cutLevelMobile={cutLevelMobile === null ? 50 : cutLevel}
      className={`mx-0 flex flex-col gap-10 lg:flex-row lg:gap-32 ${className}`}
      id={id}
    >
      <div className="flex flex-col gap-4 lg:w-1/2 lg:gap-8">
        <SectionText
          textAlign="start"
          variant={textColor}
          direction="column"
          subtitle={subTitle}
          desc={desc}
          title={title}
          caption={caption}
          showSeeMore
          className="w-auto"
        />

        <div className="hidden flex-col gap-10 lg:flex lg:flex-row">
          {button?.iframe && (
            <>
              <PopUpIframe
                className="rounded-md bg-white"
                setState={popUpToggle}
                state={popUpState}
                src={button.iframe}
              />

              <Button
                onClick={popUpToggle}
                size="large"
                variant={button.variant}
              >
                {button.label}
              </Button>
            </>
          )}
          {link?.label && link?.src && (
            <NextLink href={link.src} passHref prefetch={false}>
              <Link arrow={link.variant !== 'outline'} variant={link.variant}>
                {link.label}
              </Link>
            </NextLink>
          )}
        </div>
      </div>
      <div className="h-64 w-full lg:mt-4 lg:h-[367px] lg:w-1/2">
        <Multimedia {...multimediaProps} />
      </div>

      <div className="flex w-full flex-col items-center justify-center gap-7 lg:hidden lg:gap-10">
        {button.iframe && (
          <Button
            onClick={popUpToggle}
            className="w-full"
            size="large"
            variant={button.variant}
          >
            {button.label}
          </Button>
        )}
        {link?.src && link?.label && (
          <NextLink passHref prefetch={false} href={link.src}>
            <Link arrow variant={link.variant} href={link.src}>
              {link.label}
            </Link>
          </NextLink>
        )}
      </div>
    </Container>
  );
};

GalleryTemplate.displayName = 'GalleryTemplate';
